// The way in, when the deployment has a password on it.
//
// A page rather than the browser's own credential box, which is what this used
// to be: that box is unstyled, cannot say *why* it is asking, and answers a
// wrong password by asking again forever. This is the first thing anyone sees,
// and the one thing worth saying at that moment is what they have arrived at.
//
// On the kit since U9 (PLAN-UI §28.0, priority 2): `Page`, a `Field`, a `Btn`
// and two `Notice`s. Nothing about the flow moved — this is the same gate in
// the language the rest of the app is now written in.

import { useState } from "react";

import { Btn, Field, Notice, Page, PageColumn, PageTitle, Text, styled } from "./kit";

/** The field and its button, on one line. */
const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "stretch",
  gap: theme.kit.space.sm,
  marginTop: theme.kit.space.lg
}));

/** Centred on the window rather than sitting at the top of a page of prose. */
const Middle = styled(PageColumn)({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center"
});

/**
 * The same gate, wherever one is needed.
 *
 * Parameterised rather than copied: there are two gates now — the deployment's
 * and the tuning dashboard's — and a second implementation would be a second
 * place for the wrong-password path to be got wrong. Everything defaults to
 * the site gate, so the original caller and every smoke that drives it are
 * untouched.
 */
export function GatePage({
  onUnlock,
  endpoint = "/api/unlock",
  title = "Factory Chess",
  blurb = "Chess, on a board that is also a factory. Captures leave scrap; pawns mine it, belts haul it, rooks smelt it into new pieces.",
  notice,
  alternative
}: {
  onUnlock(): void;
  endpoint?: string;
  title?: string;
  blurb?: string;
  notice?: React.ReactNode;
  /** A second way in beside the password — the site gate's Google sign-in. */
  alternative?: React.ReactNode;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  /** Nothing wrong yet, the password was refused, or nobody answered. */
  const [failed, setFailed] = useState<null | "wrong" | "unreachable">(null);

  const submit = async () => {
    if (busy || password.length === 0) return;
    setBusy(true);
    setFailed(null);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ password })
      });
      if (response.ok) onUnlock();
      // **A refusal and a broken server are not the same thing to say.** They
      // used to be — anything but `ok` read as "that is not the password" —
      // and it sent somebody typing the *correct* password round in circles
      // being told they had it wrong, because the endpoint behind the dev
      // proxy was answering 500. A 4xx is the server refusing; anything else
      // is the server not working.
      else setFailed(response.status >= 400 && response.status < 500 ? "wrong" : "unreachable");
    } catch {
      setFailed("unreachable");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page data-testid="gate-page">
      <Middle width={460}>
        <PageTitle>{title}</PageTitle>
        <Text tone="dim" block size={13}>
          {blurb}
        </Text>

        {/*
          Said plainly and before the field, because it is the reason there is
          a field at all. Somebody who was sent a link deserves to know what
          they have arrived at before being asked for anything.
        */}
        <Notice tone="warn" data-testid="gate-notice" style={{ marginTop: 18 }}>
          {notice ?? (
            <>
              <strong>This is a prototype.</strong> It is in active development,
              it changes daily, and games in progress may be lost when it does.
              Access is limited to playtesters for now — if you were given a
              password, this is where it goes.
            </>
          )}
        </Notice>

        <Row>
          <Field
            autoFocus
            type="password"
            placeholder="Password"
            aria-label="Password"
            value={password}
            invalid={failed !== null}
            disabled={busy}
            data-testid="gate-input"
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && void submit()}
          />
          <Btn
            variant="primary"
            data-testid="gate-go"
            disabled={busy || password.length === 0}
            onClick={() => void submit()}
          >
            {busy ? "…" : "Enter"}
          </Btn>
        </Row>

        {failed !== null && (
          <Notice tone="bad" data-testid="gate-error" style={{ marginTop: 12 }}>
            {failed === "wrong"
              ? "That is not the password. Try again."
              : "Could not reach the server, so the password could not be checked."}
          </Notice>
        )}

        {alternative && (
          <>
            <Text tone="dim" block size={12} style={{ marginTop: 22, textAlign: "center" }}>
              or
            </Text>
            {alternative}
          </>
        )}
      </Middle>
    </Page>
  );
}
